import { useState, useEffect, useCallback } from 'react';
import { fetchJson } from '@shared/api';
import { SimpleCache } from '@shared/api/cache';
import { CACHE_TTL, CACHE_LIMITS } from '@shared/config';
import type { Category } from '../types';

const categoryCache = new SimpleCache<Category[]>({
  defaultTtl: CACHE_TTL.CATEGORY,
  maxEntries: CACHE_LIMITS.CATEGORY_MAX_ENTRIES,
});

interface UseCategoriesResult {
  categories: Category[];
  loading: boolean;
  error: string | null;
}

export function useCategories(contentType?: string): UseCategoriesResult {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const cacheKey = contentType ? `categories-${contentType}` : 'categories';
      const url = contentType
        ? `/api/categories?contentType=${contentType}`
        : '/api/categories';
      const data = await categoryCache.getOrSet(cacheKey, async () => {
        const response = await fetchJson<{ categories: Category[] }>(url);
        return response.categories;
      });
      setCategories(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [contentType]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return { categories, loading, error };
}
